import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import backIcon from '../../../../../assets/icons/back-arrow.png';
import { scoringService } from '../../../../../services';
import './DashboardScore.css';
import './ScoreShoot.css';

const ClayScoreSummary = () => {
    const { scoreSheetId } = useParams();
    const navigate = useNavigate();
    const [results, setResults] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isFinishing, setIsFinishing] = useState(false);
    const [error, setError] = useState(null);

    const fetchResults = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const data = await scoringService.getScoreSheetResults(scoreSheetId);
            setResults(data);
        } catch (fetchError) {
            console.error('Error loading clay results:', fetchError);
            setError('Failed to load results summary.');
        } finally {
            setIsLoading(false);
        }
    }, [scoreSheetId]);

    useEffect(() => {
        if (scoreSheetId) {
            fetchResults();
        }
    }, [scoreSheetId, fetchResults]);

    const finishScoreSheet = async () => {
        setIsFinishing(true);
        setError(null);

        try {
            const data = await scoringService.finishScoreSheet(scoreSheetId);
            setResults(data);
        } catch (finishError) {
            console.error('Error finishing score sheet:', finishError);
            setError('Failed to finalise this score sheet.');
        } finally {
            setIsFinishing(false);
        }
    };

    const isFunShoot = useMemo(
        () => results?.scoreSheetType === 'FunShoot' || results?.variant === 'FunShoot',
        [results]
    );

    if (isLoading) {
        return <div className="loading-container">Loading summary...</div>;
    }

    if (error && !results) {
        return <div className="error-container">{error}</div>;
    }

    if (!results) {
        return <div className="error-container">No summary available for this score sheet.</div>;
    }

    return (
        <div className="score-shoot-container">
            <div className="score-header">
                <button
                    onClick={() => navigate(
                        isFunShoot
                            ? `/dashboard/score/fun-shoot/${scoreSheetId}`
                            : `/dashboard/score/score-shoot/${scoreSheetId}`
                    )}
                    className="back-button"
                >
                    <img src={backIcon} alt="Back" />
                </button>
                <h2>{results.eventName || 'Clay Results'} Summary</h2>
            </div>

            {error && <div className="error-container">{error}</div>}

            <div className="score-summary-card">
                <div className="score-summary-content">
                    <div className="score-summary-item">
                        <span className="score-label">Session</span>
                        <span className="score-value">{results.totalHits}/{results.totalShotsCompleted}</span>
                    </div>
                    <div className="score-summary-item">
                        <span className="score-label">Overall %</span>
                        <span className="score-value">{results.overallPercentage}%</span>
                    </div>
                    <div className="score-summary-item">
                        <span className="score-label">Shooters</span>
                        <span className="score-value">{results.shooters?.length || 0}</span>
                    </div>
                    <div className="score-summary-item">
                        <span className="score-label">Status</span>
                        <span className="score-value">{results.isFinished ? 'Final' : 'Live'}</span>
                    </div>
                </div>
            </div>

            <div className="instructions-card">
                <p>
                    {results.isFinished
                        ? `Finalised by ${results.verifiedBy || 'ShootConnect'}.`
                        : 'Review the standings and stand breakdown, then finalise once you are happy with the score card.'}
                </p>
                <p>
                    {results.disciplineType} {results.variant ? `- ${results.variant}` : ''}
                    {results.standName ? ` • ${results.standName}` : ''}
                </p>
            </div>

            <div className="clay-summary-section">
                <div className="clay-summary-header-row">
                    <h3>Shooter Results</h3>
                    {!results.isFinished && (
                        <button
                            type="button"
                            className="finish-button"
                            onClick={finishScoreSheet}
                            disabled={isFinishing}
                        >
                            {isFinishing ? 'Finalising...' : 'Finalise Score Sheet'}
                        </button>
                    )}
                </div>
                <div className="clay-summary-grid">
                    {(results.shooters || []).map((shooter) => (
                        <div key={`${shooter.position}-${shooter.shooterName}`} className="clay-summary-card">
                            <div className="clay-summary-card-head">
                                <div>
                                    <span className="clay-summary-rank">#{shooter.position}</span>
                                    <h4>{shooter.shooterName}</h4>
                                </div>
                                <strong>{shooter.hits}/{shooter.shotsCompleted}</strong>
                            </div>
                            <div className="clay-summary-meta">
                                <span>{shooter.percentage}%</span>
                                <span>{shooter.pendingShots} pending</span>
                                <span>{shooter.totalShots} planned</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="clay-summary-section">
                <h3>Stand Breakdown</h3>
                <div className="clay-stand-results">
                    {(results.stands || []).map((stand) => (
                        <div key={stand.standKey} className="clay-stand-result-row">
                            <div>
                                <strong>{stand.standName || `Stand ${stand.standKey}`}</strong>
                                {stand.standName !== stand.standKey && <small>Stand {stand.standKey}</small>}
                            </div>
                            <div className="clay-stand-result-stats">
                                <strong>{stand.hits}/{stand.shotsCompleted}</strong>
                                <span>{stand.percentage}%</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="clay-summary-actions">
                <button
                    type="button"
                    className="competitive-nav-btn"
                    onClick={() => navigate('/dashboard/score')}
                >
                    Back To Score Dashboard
                </button>
                <button
                    type="button"
                    className="competitive-focus-btn"
                    onClick={() => navigate(
                        isFunShoot
                            ? `/dashboard/score/fun-shoot/${scoreSheetId}`
                            : `/dashboard/score/score-shoot/${scoreSheetId}`
                    )}
                >
                    Return To Scoring
                </button>
            </div>
        </div>
    );
};

export default ClayScoreSummary;
